import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import type { Prng } from '@learnforge/demo-math/numeric';
import {
  type Expr,
  Fraction,
  Polynomial,
  num,
  variable,
  add,
  mul,
  pow,
  func,
  canonicalise,
  format,
} from '../algebra';
import type { AlgebraContext, AlgebraKey } from '../types';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianDomain,
} from '@learnforge/demo-render';
import { mountActionBar } from '../action-bar';
import { QUADRATIC_PRESETS } from '../data/quadratic-presets';
import { createParabolaSketch, fitParabolaDomains } from '../render/parabola-sketch';

// ============================================================================
// Type definitions
// ============================================================================

export interface CompleteSquareStep {
  description: string;
  expr: Expr;
}

export interface QuadraticRootsResult {
  roots: Expr[];
  discriminant: Fraction;
  nature: 'two-real' | 'one-real' | 'complex-pair';
}

export interface CompleteSquareResult {
  h: Fraction;
  k: Fraction;
  steps: CompleteSquareStep[];
}

export interface FactorQuadraticResult {
  factors: [Polynomial, Polynomial];
}

export interface FactorQuadraticIrreducible {
  irreducible: true;
  reason: 'irrational' | 'complex';
}

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

/**
 * Compute discriminant and nature of quadratic roots.
 * Δ = b² − 4ac
 * If Δ > 0 and perfect square: two distinct rational roots
 * If Δ > 0 and not perfect square: two distinct irrational roots (√Δ appears in formula)
 * If Δ = 0: one real root (double)
 * If Δ < 0: complex conjugate pair
 */
export function quadraticRoots(a: Fraction, b: Fraction, c: Fraction): QuadraticRootsResult {
  const discriminant = b.mul(b).sub(a.mul(c).mul(new Fraction(4, 1)));

  if (discriminant.toNumber() > 0) {
    // Check if perfect square
    const sqrtVal = Math.sqrt(discriminant.toNumber());
    const isPerfectSquare = Number.isInteger(sqrtVal);

    if (isPerfectSquare) {
      // Two rational roots: (-b ± √Δ) / (2a)
      const sqrtDiscriminant = new Fraction(Math.floor(sqrtVal), 1);
      const twoA = a.mul(new Fraction(2, 1));
      const root1 = b.neg().add(sqrtDiscriminant).div(twoA);
      const root2 = b.neg().sub(sqrtDiscriminant).div(twoA);
      return {
        roots: [num(root1), num(root2)],
        discriminant,
        nature: 'two-real',
      };
    } else {
      // Two irrational roots: (-b ± √Δ) / (2a) as Expr
      // Special case: if b = 0 (no linear term), simplify to ±√(-c/a)
      if (b.num === 0) {
        const quotient = c.neg().div(a);
        const root1Expr = func('sqrt', num(quotient));
        const root2Expr = mul(num(-1), func('sqrt', num(quotient)));
        return {
          roots: [canonicalise(root1Expr), canonicalise(root2Expr)],
          discriminant,
          nature: 'two-real',
        };
      }

      const twoA = a.mul(new Fraction(2, 1));
      const negB = b.neg();
      const sqrtDelta = func('sqrt', num(discriminant));
      const divisor = num(new Fraction(1, 1).div(twoA));

      // Properly formatted: (-b + √Δ) / (2a) and (-b - √Δ) / (2a)
      const root1Expr = mul(divisor, add(num(negB), sqrtDelta));
      const root2Expr = mul(divisor, add(num(negB), mul(num(-1), sqrtDelta)));

      return {
        roots: [canonicalise(root1Expr), canonicalise(root2Expr)],
        discriminant,
        nature: 'two-real',
      };
    }
  } else if (discriminant.toNumber() === 0) {
    // One real root: -b / (2a)
    const twoA = a.mul(new Fraction(2, 1));
    const root = b.neg().div(twoA);
    return {
      roots: [num(root)],
      discriminant,
      nature: 'one-real',
    };
  } else {
    // Complex pair: (-b ± i√|Δ|) / (2a)
    const twoA = a.mul(new Fraction(2, 1));
    const negB = b.neg();
    const absDelta = discriminant.neg();
    const i = variable('i');
    const sqrtAbsDelta = func('sqrt', num(absDelta));
    const divisor = num(new Fraction(1, 1).div(twoA));

    const root1Expr = mul(divisor, add(num(negB), mul(i, sqrtAbsDelta)));
    const root2Expr = mul(divisor, add(num(negB), mul(num(-1), mul(i, sqrtAbsDelta))));

    return {
      roots: [canonicalise(root1Expr), canonicalise(root2Expr)],
      discriminant,
      nature: 'complex-pair',
    };
  }
}

/**
 * Complete the square: ax² + bx + c = a(x + h)² + k
 * h = b / (2a)
 * k = c - b² / (4a)
 */
export function completeTheSquare(a: Fraction, b: Fraction, c: Fraction): CompleteSquareResult {
  const steps: CompleteSquareStep[] = [];

  const h = b.div(a.mul(new Fraction(2, 1)));
  const k = c.sub(b.mul(b).div(a.mul(new Fraction(4, 1))));

  // Step 1: Original form
  steps.push({
    description: 'Original form',
    expr: add(mul(num(a), pow(variable('x'), num(2))), mul(num(b), variable('x')), num(c)),
  });

  // Step 2: Factor out a from first two terms
  steps.push({
    description: 'Factor out a',
    expr: add(
      mul(num(a), add(pow(variable('x'), num(2)), mul(num(b.div(a)), variable('x')))),
      num(c),
    ),
  });

  // Step 3: Complete the square inside parentheses
  const hSquared = h.mul(h);
  steps.push({
    description: 'Complete the square',
    expr: add(
      mul(
        num(a),
        add(pow(add(variable('x'), num(h)), num(2)), num(new Fraction(-1, 1).mul(hSquared))),
      ),
      num(c),
    ),
  });

  // Step 4: Simplify to vertex form
  steps.push({
    description: 'vertex form: a(x + h)² + k',
    expr: add(mul(num(a), pow(add(variable('x'), num(h)), num(2))), num(k)),
  });

  return { h, k, steps };
}

/**
 * Factor a quadratic over ℚ (rationals).
 * If Δ is a perfect square and the roots are rational, return the factors.
 * Otherwise return irreducible with the reason.
 */
export function factorQuadraticOverQ(
  a: Fraction,
  b: Fraction,
  c: Fraction,
): FactorQuadraticResult | FactorQuadraticIrreducible {
  const discriminant = b.mul(b).sub(a.mul(c).mul(new Fraction(4, 1)));

  // Check if Δ is negative (complex roots)
  if (discriminant.toNumber() < 0) {
    return {
      irreducible: true,
      reason: 'complex',
    };
  }

  // Check if Δ is a perfect rational square
  const sqrtVal = Math.sqrt(discriminant.toNumber());
  if (!Number.isInteger(sqrtVal)) {
    return {
      irreducible: true,
      reason: 'irrational',
    };
  }

  // Δ is a perfect square; compute roots
  const sqrtDiscriminant = new Fraction(Math.floor(sqrtVal), 1);
  const twoA = a.mul(new Fraction(2, 1));
  const root1 = b.neg().add(sqrtDiscriminant).div(twoA);
  const root2 = b.neg().sub(sqrtDiscriminant).div(twoA);

  // Build linear factors: (x - root1)(x - root2)
  // If a ≠ 1, the leading coefficient is included in the first factor
  const poly1Coeffs = [root1.neg(), a];
  const poly2Coeffs = [root2.neg(), Fraction.ONE];

  const factor1 = new Polynomial(poly1Coeffs, 'x');
  const factor2 = new Polynomial(poly2Coeffs, 'x');

  return {
    factors: [factor1, factor2],
  };
}

/**
 * Verify Vieta's formulas: sum of roots = -b/a, product = c/a
 */
export function vietaCheck(
  a: Fraction,
  b: Fraction,
  c: Fraction,
  roots: readonly Fraction[],
): boolean {
  if (roots.length === 0) return false;

  // Sum of roots
  let sum = Fraction.ZERO;
  for (const root of roots) {
    sum = sum.add(root);
  }

  // Product of roots
  let product = Fraction.ONE;
  for (const root of roots) {
    product = product.mul(root);
  }

  const expectedSum = b.neg().div(a);
  const expectedProduct = c.div(a);

  return sum.equals(expectedSum) && product.equals(expectedProduct);
}

/**
 * Run all three methods and return their results.
 * All methods should yield the same solution set when they succeed.
 */
export function runAllThreeMethods(
  a: Fraction,
  b: Fraction,
  c: Fraction,
): {
  factoring: FactorQuadraticResult | FactorQuadraticIrreducible;
  completingSquare: CompleteSquareResult;
  formula: QuadraticRootsResult;
} {
  return {
    factoring: factorQuadraticOverQ(a, b, c),
    completingSquare: completeTheSquare(a, b, c),
    formula: quadraticRoots(a, b, c),
  };
}

/**
 * Procedurally generate a random quadratic ax² + bx + c.
 * Three branches, weighted so most quadratics factor cleanly:
 * - kind < 14: two integer roots in −6..6, small leading coefficient
 * - 14 ≤ kind < 17: perfect square (double root)
 * - kind ≥ 17: irreducible over ℝ (Δ < 0 forced by c > b²/4)
 */
export function randomQuadratic(prng: Prng): { a: Fraction; b: Fraction; c: Fraction } {
  const kind = prng(20);
  if (kind < 14) {
    // two integer roots in −6..6, small leading coefficient
    const r1 = prng(13) - 6;
    const r2 = prng(13) - 6;
    const aChoices = [1, 1, 1, 2, 3];
    const a = new Fraction(aChoices[prng(5)]!, 1);
    return { a, b: a.mul(new Fraction(-(r1 + r2), 1)), c: a.mul(new Fraction(r1 * r2, 1)) };
  }
  if (kind < 17) {
    // perfect-square (double root)
    const r = prng(11) - 5;
    return { a: Fraction.ONE, b: new Fraction(-2 * r, 1), c: new Fraction(r * r, 1) };
  }
  // irreducible over ℝ: c > b²/4 forces Δ < 0
  const b = prng(7) - 3;
  const c = Math.floor((b * b) / 4) + 1 + prng(3);
  return { a: Fraction.ONE, b: new Fraction(b, 1), c: new Fraction(c, 1) };
}

// ============================================================================
// Method Card Line Builders (exported and tested)
// ============================================================================

/**
 * Build the display lines for the factoring card.
 * Irreducible: report the discriminant and why no rational factorisation exists.
 * Factorable: derive p, q from Vieta's formulas (p + q = b, p·q = a·c), then the
 * factored form and roots.
 */
export function factoringLines(a: Fraction, b: Fraction, c: Fraction): string[] {
  const lines: string[] = [];
  const factoringResult = factorQuadraticOverQ(a, b, c);

  if ('irreducible' in factoringResult) {
    const discriminant = b.mul(b).sub(a.mul(c).mul(new Fraction(4, 1)));
    if (factoringResult.reason === 'complex') {
      lines.push(`Δ = ${discriminant.toString()} < 0`);
    } else {
      lines.push(`Δ = ${discriminant.toString()} not a perfect square`);
    }
    lines.push('No rational factorisation');
    return lines;
  }

  const { factors } = factoringResult;
  const roots = quadraticRoots(a, b, c);
  const rootValue = (expr: Expr): Fraction => (expr.kind === 'num' ? expr.value : Fraction.ZERO);
  const r1 = rootValue(roots.roots[0]!);
  const r2 = roots.roots.length === 2 ? rootValue(roots.roots[1]!) : r1;
  const p = a.mul(r1).neg();
  const q = a.mul(r2).neg();

  lines.push(`p + q = b = ${b.toString()}`);
  lines.push(`p · q = a·c = ${a.mul(c).toString()}`);
  lines.push(`p = ${p.toString()},  q = ${q.toString()}`);

  const factor1Str = format(factors[0].toExpr());
  const factor2Str = format(factors[1].toExpr());
  lines.push(`(${factor1Str})(${factor2Str}) = 0`);

  if (roots.roots.length === 2) {
    lines.push(`x = ${format(roots.roots[0]!)}`);
    lines.push(`x = ${format(roots.roots[1]!)}`);
  } else if (roots.roots.length === 1) {
    lines.push(`x = ${format(roots.roots[0]!)}`);
  }

  return lines;
}

/**
 * Build the display lines for the quadratic-formula card: the general rule,
 * the substituted discriminant, the substituted formula, then the roots.
 */
export function formulaLines(a: Fraction, b: Fraction, c: Fraction): string[] {
  const lines: string[] = [];
  const discriminant = b.mul(b).sub(a.mul(c).mul(new Fraction(4, 1)));
  const formulaResult = quadraticRoots(a, b, c);

  lines.push('x = (-b ± √Δ) / 2a');
  lines.push(
    `Δ = (${b.toString()})² - 4·${a.toString()}·${c.toString()} = ${discriminant.toString()}`,
  );

  const nB = b.neg();
  const twoA = a.mul(new Fraction(2, 1));
  // Literal substitution (unsimplified) — the simplified roots follow below.
  const sqrtDeltaStr = `√${discriminant.toString()}`;
  const denominatorStr = twoA.toString();
  lines.push(`x = (${nB.toString()} ± ${sqrtDeltaStr}) / ${denominatorStr}`);

  for (const root of formulaResult.roots) {
    lines.push(`x = ${format(root, { useUnicodeSuperscripts: true })}`);
  }

  return lines;
}

/**
 * Build the display lines for the completing-the-square card: the general
 * rule, each derivation step from completeTheSquare(), the solve-for-x lines,
 * then the vertex.
 */
export function completingSquareLines(a: Fraction, b: Fraction, c: Fraction): string[] {
  const lines: string[] = ['a(x + h)² + k,  h = b/2a,  k = c - b²/4a'];
  const csResult = completeTheSquare(a, b, c);

  for (const step of csResult.steps) {
    const formatted = format(step.expr, { useUnicodeSuperscripts: true });
    if (formatted === lines[lines.length - 1]) continue;
    lines.push(formatted);
  }

  const minusK = csResult.k.neg();
  const quotient = minusK.div(a);
  const sqrtExpr = func('sqrt', num(quotient));
  const sqrtFormatted = format(sqrtExpr, { useUnicodeSuperscripts: true });

  if (csResult.h.num === 0) {
    lines.push(`x = ±${sqrtFormatted}`);
  } else {
    const hFormatted = format(num(csResult.h), { useUnicodeSuperscripts: true });
    lines.push(`x + (${hFormatted}) = ±${sqrtFormatted}`);
    lines.push(`x = ${hFormatted} ± ${sqrtFormatted}`);
  }

  lines.push(`vertex: (${csResult.h.toString()}, ${csResult.k.toString()})`);

  return lines;
}

// ============================================================================
// SVG Rendering Helper
// ============================================================================

/**
 * Build a readable quadratic equation string from coefficients.
 * Renders as canonicalised expr via format() so zero terms vanish.
 * Example: x² − 5x + 6 = 0
 */
function formatQuadraticExpression(a: Fraction, b: Fraction, c: Fraction): string {
  const expr = add(mul(num(a), pow(variable('x'), num(2))), mul(num(b), variable('x')), num(c));
  const canonForm = canonicalise(expr);
  return format(canonForm, { useUnicodeSuperscripts: true });
}

function formatQuadraticEquation(a: Fraction, b: Fraction, c: Fraction): string {
  return `${formatQuadraticExpression(a, b, c)} = 0`;
}

/**
 * Greedy word-wrap: split a line at whitespace boundaries so each row fits
 * within maxChars. Used to keep long card lines from overflowing the fixed
 * 250px pane width — the logical `lines[]` arrays stay one entry per idea,
 * this only affects how a single entry is laid out across SVG text rows.
 */
function wrapLine(line: string, maxChars: number): string[] {
  if (line.length <= maxChars) return [line];

  const words = line.split(' ');
  const rows: string[] = [];
  let current = '';

  for (const word of words) {
    const candidate = current.length === 0 ? word : `${current} ${word}`;
    if (candidate.length > maxChars && current.length > 0) {
      rows.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current.length > 0) rows.push(current);

  return rows;
}

/**
 * Render a collapsible pane with a toggleable header and stacked text lines.
 * startX, startY are the top-left corner. Collapsed panes render only the
 * 36px header (title + chevron); expanded panes also render the lines.
 */
function renderPane(
  g: SVGGElement,
  title: string,
  lines: string[],
  startX: number,
  startY: number,
  width: number,
  height: number,
  expanded: boolean,
  onToggle: () => void,
): void {
  const HEADER_H = 36;

  const bg = createSvgEl('rect', {
    x: startX,
    y: startY,
    width,
    height: expanded ? height : HEADER_H,
    class: 'alg-card',
  });
  g.appendChild(bg);

  // Header — the toggle target (mouse + keyboard)
  const header = createSvgEl('g', {
    class: 'alg-card-toggle',
    role: 'button',
    tabindex: '0',
    'aria-expanded': expanded ? 'true' : 'false',
  });
  const hit = createSvgEl('rect', {
    x: startX,
    y: startY,
    width,
    height: HEADER_H,
    class: 'alg-card-hit',
  });
  header.appendChild(hit);

  const titleEl = createSvgEl('text', {
    x: startX + 10,
    y: startY + 24,
    class: 'alg-title alg-title--accent',
  });
  titleEl.textContent = title;
  header.appendChild(titleEl);

  // Chevron: ▸ collapsed, ▾ expanded (mirrors demo-kit's collapsible panels)
  const cx = startX + width - 26;
  const cy = startY + 11;
  const points = expanded
    ? `${cx},${cy + 3} ${cx + 12},${cy + 3} ${cx + 6},${cy + 12}`
    : `${cx + 2},${cy} ${cx + 11},${cy + 7} ${cx + 2},${cy + 14}`;
  header.appendChild(createSvgEl('polygon', { points, class: 'alg-card-chevron' }));

  header.addEventListener('click', onToggle);
  header.addEventListener('keydown', (e) => {
    const key = e.key;
    if (key === 'Enter' || key === ' ') {
      e.preventDefault();
      onToggle();
    }
  });
  g.appendChild(header);

  if (!expanded) return;

  // Lines — wrap entries too wide for the pane; 22px rows fit the 295px card.
  let currentY = startY + 58;
  const lineHeight = 22;
  const maxCharsPerLine = 27;

  for (const line of lines) {
    for (const row of wrapLine(line, maxCharsPerLine)) {
      const lineEl = createSvgEl('text', {
        x: startX + 10,
        y: currentY,
        class: 'alg-text',
      });
      lineEl.textContent = row;
      g.appendChild(lineEl);
      currentY += lineHeight;
    }
  }
}

// ============================================================================
// State
// ============================================================================

interface State {
  presetId: string;
  a: string;
  b: string;
  c: string;
  expandedCards: { factoring: boolean; completingSquare: boolean; formula: boolean };
}

// ============================================================================
// Spec factory
// ============================================================================

// Helper to parse preset value into Fraction
function parseFractionValue(val: number | string): Fraction {
  if (typeof val === 'number') {
    return new Fraction(val, 1);
  }
  return Fraction.parse(val);
}

function buildSpec(): CustomExperimentSpec<State, AlgebraKey, AlgebraContext> {
  return {
    labelKey: 'experiment.quadratic-workshop.label',
    demoId: 'algebra',
    advancedKey: 'quadratic-workshop',

    initialState(): State {
      const defaultPreset = QUADRATIC_PRESETS[0]!;
      const fa = parseFractionValue(defaultPreset.a);
      const fb = parseFractionValue(defaultPreset.b);
      const fc = parseFractionValue(defaultPreset.c);
      return {
        presetId: defaultPreset.id,
        a: fa.toString(),
        b: fb.toString(),
        c: fc.toString(),
        expandedCards: { factoring: false, completingSquare: false, formula: false },
      };
    },

    applyPreset(state, presetId): State {
      const preset = QUADRATIC_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      const fa = parseFractionValue(preset.a);
      const fb = parseFractionValue(preset.b);
      const fc = parseFractionValue(preset.c);
      return {
        ...state,
        presetId: preset.id,
        a: fa.toString(),
        b: fb.toString(),
        c: fc.toString(),
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', {
        viewBox: '0 0 800 620',
        class: 'alg-quadratic-workshop-svg',
      });
      host.appendChild(svg);
      const legend = createFormulaLegend(host);
      legend.element.classList.add('alg-formula-legend--quadratic');
      let resetView = (): void => {};

      // Create group containers for each pane — created ONCE, cleared each update
      const header = createSvgEl('g', { id: 'quadratic-header' });
      const factoringPane = createSvgEl('g', { id: 'factoring-pane' });
      const completingSquarePane = createSvgEl('g', { id: 'completing-square-pane' });
      const formulaPane = createSvgEl('g', { id: 'formula-pane' });

      svg.appendChild(header);
      svg.appendChild(factoringPane);
      svg.appendChild(completingSquarePane);
      svg.appendChild(formulaPane);

      // Full-width function-graph sketch, top panel of the workshop
      const sketch = createParabolaSketch(svg, { a: 1, b: 0, c: 0, width: 760, height: 250 });
      sketch.group.setAttribute('transform', 'translate(20, 45)');
      let coefficients = { a: 1, b: 0, c: 0 };
      let visibleDomains: CartesianDomain = fitParabolaDomains(1, 0, 0);
      const viewport = createCartesianViewport(svg, {
        base: visibleDomains,
        plotRect: { x: 26, y: 51, width: 748, height: 238 },
        touchActionTarget: sketch.group,
        onChange(next) {
          visibleDomains = next;
          sketch.update({ ...coefficients, ...visibleDomains });
        },
      });
      resetView = () => viewport.reset();
      let lastPresetId: string | null = null;
      let refitOnNextUpdate = false;

      // ── DOM: Panel ──

      // Coefficient inputs
      const coefficientRow = document.createElement('div');
      coefficientRow.className = 'alg-panel-row alg-panel-row--inline';

      const aLabel = document.createElement('label');
      aLabel.textContent = 'a:';
      const aInput = document.createElement('input');
      aInput.type = 'text';
      aInput.className = 'alg-text-input';

      const bLabel = document.createElement('label');
      bLabel.textContent = 'b:';
      const bInput = document.createElement('input');
      bInput.type = 'text';
      bInput.className = 'alg-text-input';

      const cLabel = document.createElement('label');
      cLabel.textContent = 'c:';
      const cInput = document.createElement('input');
      cInput.type = 'text';
      cInput.className = 'alg-text-input';

      coefficientRow.appendChild(aLabel);
      coefficientRow.appendChild(aInput);
      coefficientRow.appendChild(bLabel);
      coefficientRow.appendChild(bInput);
      coefficientRow.appendChild(cLabel);
      coefficientRow.appendChild(cInput);
      ctx.panel.appendChild(coefficientRow);

      // Input change handlers — wire BOTH 'input' and 'change' for live re-render
      const handleInputChange = (): void => {
        try {
          // Validate by parsing; only push state if all three parse successfully
          parseFractionValue(aInput.value);
          parseFractionValue(bInput.value);
          parseFractionValue(cInput.value);
          controls.setState((s) => ({
            ...s,
            a: aInput.value,
            b: bInput.value,
            c: cInput.value,
          }));
        } catch {
          // Silent: invalid input leaves render unchanged
        }
      };

      aInput.addEventListener('input', handleInputChange);
      aInput.addEventListener('change', handleInputChange);
      bInput.addEventListener('input', handleInputChange);
      bInput.addEventListener('change', handleInputChange);
      cInput.addEventListener('input', handleInputChange);
      cInput.addEventListener('change', handleInputChange);

      // ── DOM: Action bar — all presets are always available (no advanced gating) ──
      const actionBar = mountActionBar(ctx.actionBar, ctx.i18n);
      actionBar.setActions([
        {
          id: 'reset',
          labelKey: 'btn.reset',
          onClick: () => {
            controls.setState((s) => {
              const preset = QUADRATIC_PRESETS.find((p) => p.id === s.presetId);
              if (!preset) return s;
              const fa = parseFractionValue(preset.a);
              const fb = parseFractionValue(preset.b);
              const fc = parseFractionValue(preset.c);
              return {
                ...s,
                a: fa.toString(),
                b: fb.toString(),
                c: fc.toString(),
              };
            });
          },
        },
        {
          id: 'view-reset',
          labelKey: 'btn.reset-view',
          onClick: () => resetView(),
        },
        {
          id: 'randomise',
          labelKey: 'btn.randomise',
          onClick: () => {
            const { a, b, c } = randomQuadratic(ctx.prng);
            refitOnNextUpdate = true;
            controls.setState((s) => ({ ...s, a: a.toString(), b: b.toString(), c: c.toString() }));
          },
        },
      ]);

      // ── RenderHandle ──
      const update = (state: State): void => {
        const a = parseFractionValue(state.a);
        const b = parseFractionValue(state.b);
        const c = parseFractionValue(state.c);
        coefficients = { a: a.toNumber(), b: b.toNumber(), c: c.toNumber() };
        if (state.presetId !== lastPresetId || refitOnNextUpdate) {
          lastPresetId = state.presetId;
          refitOnNextUpdate = false;
          visibleDomains = fitParabolaDomains(coefficients.a, coefficients.b, coefficients.c);
          viewport.setBase(visibleDomains, { notify: false });
        }

        // Guard value-sync to preserve caret position
        if (aInput.value !== state.a) aInput.value = state.a;
        if (bInput.value !== state.b) bInput.value = state.b;
        if (cInput.value !== state.c) cInput.value = state.c;

        // Clear all panes and rebuild
        header.replaceChildren();
        factoringPane.replaceChildren();
        completingSquarePane.replaceChildren();
        formulaPane.replaceChildren();

        // Header: equation and discriminant
        const eqnStr = formatQuadraticEquation(a, b, c);
        legend.setEntries([
          {
            id: 'quadratic',
            formula: `y = ${formatQuadraticExpression(a, b, c)}`,
            color: 'var(--ink)',
          },
        ]);
        const eqnText = createSvgEl('text', {
          x: 200,
          y: 30,
          class: 'alg-title',
          'text-anchor': 'middle',
        });
        eqnText.textContent = eqnStr;
        header.appendChild(eqnText);

        const discriminant = b.mul(b).sub(a.mul(c).mul(new Fraction(4, 1)));
        const discriminantStr = `∆ = b² − 4ac = ${discriminant.toString()}`;
        const discriminantText = createSvgEl('text', {
          x: 590,
          y: 30,
          class: 'alg-text',
          'text-anchor': 'middle',
        });
        discriminantText.textContent = discriminantStr;
        header.appendChild(discriminantText);

        // Parabola sketch: mirrors the current coefficients
        sketch.update({ ...coefficients, ...visibleDomains });

        // Factoring pane
        renderPane(
          factoringPane,
          ctx.i18n.t('method.factoring'),
          factoringLines(a, b, c),
          20,
          315,
          250,
          295,
          state.expandedCards.factoring,
          () =>
            controls.setState((s) => ({
              ...s,
              expandedCards: { ...s.expandedCards, factoring: !s.expandedCards.factoring },
            })),
        );

        // Completing-the-square pane
        renderPane(
          completingSquarePane,
          ctx.i18n.t('method.completing-square'),
          completingSquareLines(a, b, c),
          285,
          315,
          250,
          295,
          state.expandedCards.completingSquare,
          () =>
            controls.setState((s) => ({
              ...s,
              expandedCards: {
                ...s.expandedCards,
                completingSquare: !s.expandedCards.completingSquare,
              },
            })),
        );

        // Formula pane
        const formulaResult = quadraticRoots(a, b, c);
        renderPane(
          formulaPane,
          ctx.i18n.t('method.quadratic-formula'),
          formulaLines(a, b, c),
          550,
          315,
          250,
          295,
          state.expandedCards.formula,
          () =>
            controls.setState((s) => ({
              ...s,
              expandedCards: { ...s.expandedCards, formula: !s.expandedCards.formula },
            })),
        );

        // Mini-Argand diagram — remove stale element then re-append if needed
        const staleArgand = svg.querySelector('#mini-argand');
        if (staleArgand) svg.removeChild(staleArgand);

        if (formulaResult.nature === 'complex-pair' && formulaResult.roots.length === 2) {
          const argandG = createSvgEl('g', { id: 'mini-argand' });
          svg.appendChild(argandG);

          const centerX = 400;
          const centerY = 545;
          const radius = 55;

          const realAxis = createSvgEl('line', {
            x1: centerX - radius,
            y1: centerY,
            x2: centerX + radius,
            y2: centerY,
            class: 'alg-axis',
            'stroke-width': 2,
          });
          argandG.appendChild(realAxis);

          const imagAxis = createSvgEl('line', {
            x1: centerX,
            y1: centerY - radius,
            x2: centerX,
            y2: centerY + radius,
            class: 'alg-axis',
            'stroke-width': 2,
          });
          argandG.appendChild(imagAxis);

          const realLabel = createSvgEl('text', {
            x: centerX + radius + 5,
            y: centerY - 5,
            class: 'alg-text-soft',
            'font-size': '12',
          });
          realLabel.textContent = 'Re';
          argandG.appendChild(realLabel);

          const imagLabel = createSvgEl('text', {
            x: centerX + 5,
            y: centerY - radius - 5,
            class: 'alg-text-soft',
            'font-size': '12',
          });
          imagLabel.textContent = 'Im';
          argandG.appendChild(imagLabel);

          const dot1 = createSvgEl('circle', {
            cx: centerX + 20,
            cy: centerY - 15,
            r: 4,
            class: 'alg-point',
          });
          argandG.appendChild(dot1);

          const dot2 = createSvgEl('circle', {
            cx: centerX + 20,
            cy: centerY + 15,
            r: 4,
            class: 'alg-point',
          });
          argandG.appendChild(dot2);

          const label = createSvgEl('text', {
            x: centerX,
            y: centerY + 30,
            class: 'alg-text-soft',
            'font-size': '12',
            'text-anchor': 'middle',
          });
          label.textContent = 'Complex conjugate pair';
          argandG.appendChild(label);
        }
      };

      return {
        update,
        dispose() {
          // GUIDANCE C: only clear actionBar (harness clears host/panel containers)
          actionBar.clear();
          viewport.dispose();
          sketch.destroy();
          legend.destroy();
        },
      };
    },

    applyShotState(state): State {
      // The reel keeps showing the methods — expand all three cards.
      return {
        ...state,
        expandedCards: { factoring: true, completingSquare: true, formula: true },
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
